// AnalysisSocialBar — like + bookmark buttons attached to existing analysis cards.
// Additive: import this inside the existing AnalysisCard.jsx WITHOUT replacing
// anything else. Provides instant feedback via optimistic UI.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Bookmark, MessageCircle, RefreshCw, Share2 } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tShadow } from "../../i18n/shadow-social";
import { useAuth } from "../../contexts/AuthContext";

import API from "../../lib/api";

export const AnalysisSocialBar = ({
  analysisId,
  initialLiked = false,
  initialLikesCount = 0,
  initialBookmarked = false,
  commentsCount = 0,
  sharesCount = 0,
  onAuthRequired,
}) => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [liked, setLiked] = useState(initialLiked);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [bookmarked, setBookmarked] = useState(initialBookmarked);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLiked(initialLiked);
    setLikesCount(initialLikesCount);
    setBookmarked(initialBookmarked);
  }, [initialLiked, initialLikesCount, initialBookmarked]);

  const toggle = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      const url =
        kind === "like"
          ? `${API}/shadow/social/like`
          : `${API}/shadow/social/bookmark`;
      const optimisticNextLiked = !liked;
      if (kind === "like") {
        setLiked(optimisticNextLiked);
        setLikesCount((n) => n + (optimisticNextLiked ? 1 : -1));
      } else {
        setBookmarked((b) => !b);
      }
      const { data } = await axios.post(
        url,
        { analysis_id: analysisId },
        { withCredentials: true }
      );
      if (kind === "like") {
        setLiked(!!data.liked);
        if (typeof data.likes_count === "number") setLikesCount(data.likes_count);
      } else {
        setBookmarked(!!data.bookmarked);
      }
    } catch (err) {
      if (err?.response?.status === 401 && onAuthRequired) onAuthRequired();
      // rollback
      if (kind === "like") {
        setLiked(initialLiked);
        setLikesCount(initialLikesCount);
      } else {
        setBookmarked(initialBookmarked);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`social-bar-${analysisId}`}
      className="mt-3 flex items-center gap-4 border-t border-white/5 pt-3 text-xs text-white/60"
    >
      <button
        type="button"
        onClick={() => toggle("like")}
        disabled={busy}
        data-testid={`social-like-${analysisId}`}
        aria-label={tShadow("like", lang)}
        className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors hover:bg-white/5 disabled:opacity-60"
        style={{ color: liked ? "#ff5577" : "rgba(255,255,255,0.6)" }}
      >
        <Heart
          className="h-4 w-4"
          fill={liked ? "currentColor" : "none"}
          strokeWidth={1.8}
        />
        <span className="tabular-nums">{likesCount}</span>
      </button>

      <button
        type="button"
        onClick={() => toggle("bookmark")}
        disabled={busy}
        data-testid={`social-bookmark-${analysisId}`}
        aria-label={tShadow("guardar", lang)}
        className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors hover:bg-white/5 disabled:opacity-60"
        style={{ color: bookmarked ? "#00d4ff" : "rgba(255,255,255,0.6)" }}
      >
        <Bookmark
          className="h-4 w-4"
          fill={bookmarked ? "currentColor" : "none"}
          strokeWidth={1.8}
        />
        <span className="text-[10px] uppercase tracking-widest">
          {bookmarked ? tShadow("guardado", lang) : tShadow("guardar", lang)}
        </span>
      </button>

      {commentsCount > 0 && (
        <span className="inline-flex items-center gap-1.5 text-white/45">
          <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
          <span className="tabular-nums">{commentsCount}</span>
        </span>
      )}

      {sharesCount > 0 && (
        <span
          className="inline-flex items-center gap-1.5 text-white/45"
          data-testid={`social-shares-${analysisId}`}
          title={`${sharesCount} share${sharesCount === 1 ? "" : "s"}`}
        >
          <Share2 className="h-4 w-4" strokeWidth={1.8} />
          <span className="tabular-nums">{sharesCount}</span>
        </span>
      )}

      {user?.is_admin && (
        <button
          type="button"
          onClick={() => {
            const postUrl = `${window.location.origin}/api/og/post/${analysisId}?v=${Date.now()}`;
            window.open(
              `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(postUrl)}`,
              "_blank",
              "noopener,noreferrer",
            );
          }}
          data-testid={`social-og-refresh-${analysisId}`}
          title="Refrescar preview OG en X (admin)"
          aria-label="Refrescar preview OG"
          className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-1 text-[#f97316]/70 transition-colors hover:bg-[#f97316]/10 hover:text-[#f97316]"
        >
          <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} />
          <span className="text-[10px] uppercase tracking-widest">OG</span>
        </button>
      )}
    </div>
  );
};

export default AnalysisSocialBar;
